import React from 'react'
import { motion } from 'framer-motion'
import { useApp } from '../contexts/AppContext'
import BankTable from './BankTable'
import Pagination from './Pagination'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const { FiDatabase, FiSearch, FiFilter, FiTrendingUp, FiUsers, FiTarget } = FiIcons

const ResultsSection = () => {
  const { 
    currentLang, 
    totalCount, 
    startIndex, 
    endIndex, 
    searchQuery,
    translations,
    shouldShowPagination
  } = useApp()

  // 構建結果計數文字
  const getResultsCountText = () => {
    if (totalCount === 0) {
      return currentLang === 'zh' ? '沒有找到結果' : 'No results found'
    }
    
    if (currentLang === 'zh') {
      return `第 ${startIndex}-${endIndex} 項，共 ${totalCount} 間銀行`
    } else {
      return `Showing ${startIndex}-${endIndex} of ${totalCount} banks`
    }
  }

  // Mobile results count - more compact
  const getMobileResultsCountText = () => {
    if (totalCount === 0) {
      return currentLang === 'zh' ? '無結果' : 'No results'
    }
    
    if (currentLang === 'zh') {
      return `${startIndex}-${endIndex} / ${totalCount}`
    } else {
      return `${startIndex}-${endIndex} of ${totalCount}`
    }
  }

  return (
    <motion.section
      id="results-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.4 }}
      className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
    >
      {/* Results Header - Mobile Optimized */}
      <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-gray-50/70 to-purple-50/30 dark:from-gray-700/70 dark:to-purple-900/20">
        {/* Mobile Layout */}
        <div className="flex items-center justify-between sm:hidden">
          <div className="flex items-center gap-2">
            <motion.div 
              className="w-6 h-6 bg-gradient-to-br from-purple-500 to-blue-600 rounded-lg flex items-center justify-center"
              whileHover={{ 
                y: -1,
                rotate: 5,
                boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.3)",
                transition: { duration: 0.2 }
              }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiDatabase} className="w-3 h-3 text-white" />
            </motion.div>
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                {currentLang === 'zh' ? '搜尋結果' : 'Results'}
              </h2>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {getMobileResultsCountText()}
              </p>
            </div>
          </div>
          
          {searchQuery && (
            <motion.div 
              className="flex items-center gap-1 text-xs text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded-md border border-purple-200 dark:border-purple-700"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <SafeIcon icon={FiSearch} className="w-3 h-3" />
              <span className="truncate max-w-[80px]">"{searchQuery}"</span>
            </motion.div>
          )}
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div 
              className="w-8 h-8 bg-gradient-to-br from-purple-500 to-blue-600 rounded-full flex items-center justify-center shadow-md"
              whileHover={{ 
                y: -2,
                rotate: 10,
                boxShadow: "0 10px 25px -5px rgba(139, 92, 246, 0.3)",
                transition: { duration: 0.2 }
              }}
              transition={{ duration: 0.2 }}
            >
              <SafeIcon icon={FiDatabase} className="w-4 h-4 text-white" />
            </motion.div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {translations.resultsTitle[currentLang]}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {getResultsCountText()}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            {searchQuery && (
              <motion.div 
                className="flex items-center gap-2 text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-3 py-2 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3 }}
                whileHover={{ 
                  y: -1,
                  boxShadow: "0 4px 12px -2px rgba(139, 92, 246, 0.25)",
                  transition: { duration: 0.2 }
                }}
              >
                <SafeIcon icon={FiSearch} className="w-4 h-4" />
                <span>"{searchQuery}"</span>
              </motion.div>
            )}
            
            {/* Enhanced Statistics */}
            {totalCount > 0 && (
              <motion.div 
                className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 rounded-lg border border-emerald-200 dark:border-emerald-700 shadow-sm"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.3, delay: 0.1 }}
                whileHover={{ 
                  y: -1,
                  boxShadow: "0 4px 12px -2px rgba(34, 197, 94, 0.25)",
                  transition: { duration: 0.2 }
                }}
              >
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                <span>{totalCount} {currentLang === 'zh' ? '個結果' : 'results'}</span>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Results Content */}
      {totalCount === 0 ? (
        <motion.div 
          className="p-6 sm:p-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <motion.div 
            className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
            whileHover={{ 
              y: -2,
              boxShadow: "0 4px 12px -2px rgba(107, 114, 128, 0.25)",
              transition: { duration: 0.2 }
            }}
            transition={{ duration: 0.2 }}
          >
            <SafeIcon icon={FiSearch} className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" />
          </motion.div>
          <h3 className="text-base sm:text-lg font-medium text-gray-900 dark:text-white mb-2">
            {translations.noResults?.title?.[currentLang] || (currentLang === 'zh' ? '沒有找到結果' : 'No Results Found')}
          </h3>
          <p className="text-sm sm:text-base text-gray-500 dark:text-gray-400">
            {translations.noResults?.message?.[currentLang] || (currentLang === 'zh' ? '請嘗試調整搜尋條件或清除篩選器' : 'Try adjusting your search terms or clear filters')}
          </p>
        </motion.div>
      ) : (
        <div className="flex flex-col">
          {/* Bank Table */}
          <div className="flex-1">
            <BankTable />
          </div>
          
          {/* Pagination - 使用 shouldShowPagination 判斷 */}
          {shouldShowPagination && <Pagination />}
        </div>
      )}
    </motion.section>
  )
}

export default ResultsSection